import { useId } from 'react';

/**
 * Tier 2 — "feature" icons.
 *
 * Bespoke, hand-drawn SVG icons for the few celebratory / reward moments of the
 * app (streak, daily goal, tournaments, podium). They share the warm, gradient,
 * dimensional language of the board pieces and avatars.
 *
 * Operational UI (navigation, actions, controls, status) must stay on Lucide —
 * never mix the two tiers inside the same icon group. See the icon-system notes.
 *
 * Each component owns unique gradient/filter ids (via useId) so multiple
 * instances on the same page (e.g. the three podium medals) don't clash.
 */

export interface FeatureIconProps {
  /** Rendered width/height in px. */
  size?: number;
  className?: string;
}

const depth = { filter: 'drop-shadow(0 6px 10px rgba(0,0,0,.35))' } as const;

/** Streak / "serie" — a layered flame with a luminous core. */
export function StreakIcon({ size = 48, className }: FeatureIconProps) {
  const id = useId();
  return (
    <svg
      width={size} height={size} viewBox="0 0 64 64"
      className={className} role="img" aria-hidden="true" style={depth}
    >
      <defs>
        <radialGradient id={`${id}-out`} cx="50%" cy="70%" r="62%">
          <stop offset="0%" stopColor="#fff3b0" />
          <stop offset="34%" stopColor="#ffce42" />
          <stop offset="66%" stopColor="#ff8a2e" />
          <stop offset="100%" stopColor="#f5341f" />
        </radialGradient>
        <linearGradient id={`${id}-core`} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#fff8db" />
          <stop offset="55%" stopColor="#ffd96b" />
          <stop offset="100%" stopColor="#ffb13d" stopOpacity="0" />
        </linearGradient>
        <filter id={`${id}-glow`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.4" />
        </filter>
      </defs>
      <path d="M32 6 C24 18 19 25 19 37 a13 13 0 0 0 26 0 C45 25 40 18 32 6 Z"
        fill="#ff5a2e" opacity="0.45" filter={`url(#${id}-glow)`} />
      <path d="M32 5 C22 19 18 26 18 38 a14 14 0 0 0 28 0 C46 27 43 23 38 16 C37 21 34 22 33 20 C35 14 34 9 32 5 Z"
        fill={`url(#${id}-out)`} />
      <path d="M32 27 C27 33 25 38 28 45 a5.5 5.5 0 0 0 8.6 -.4 C39 39 37 33 32 27 Z"
        fill={`url(#${id}-core)`} />
      <ellipse cx="27" cy="22" rx="2.4" ry="3.6" fill="#fff7d6" opacity="0.7" />
    </svg>
  );
}

/** Daily goal — a target with a metallic arrow hitting the bullseye. */
export function GoalIcon({ size = 48, className }: FeatureIconProps) {
  const id = useId();
  return (
    <svg
      width={size} height={size} viewBox="0 0 64 64"
      className={className} role="img" aria-hidden="true" style={depth}
    >
      <defs>
        <radialGradient id={`${id}-ring`} cx="42%" cy="36%" r="72%">
          <stop offset="0%" stopColor="#7df0c8" />
          <stop offset="55%" stopColor="#19b783" />
          <stop offset="100%" stopColor="#0a7a55" />
        </radialGradient>
        <linearGradient id={`${id}-shaft`} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#c7cede" />
          <stop offset="100%" stopColor="#8b93ab" />
        </linearGradient>
      </defs>
      <circle cx="32" cy="32" r="25" fill={`url(#${id}-ring)`} />
      <circle cx="32" cy="32" r="18.5" fill="#fdfcf4" />
      <circle cx="32" cy="32" r="12.5" fill={`url(#${id}-ring)`} />
      <circle cx="32" cy="32" r="6.5" fill="#fdfcf4" />
      <circle cx="32" cy="32" r="3" fill="#f0452f" />
      <ellipse cx="24" cy="22" rx="5" ry="8" fill="#fff" opacity="0.18" />
      <line x1="40" y1="40" x2="62" y2="62" stroke={`url(#${id}-shaft)`} strokeWidth="3.4" strokeLinecap="round" />
      <path d="M59 59 L63 63 L61 56 Z" fill="#ff5a3c" />
      <path d="M58 62 l4 -1 l-1 4 Z" fill="#ff8a5b" />
      <circle cx="40" cy="40" r="3" fill="#f0452f" stroke="#fff" strokeWidth="1.4" />
    </svg>
  );
}

/** Tournament / live event — a gold cup with handles, star and base. */
export function TrophyIcon({ size = 48, className }: FeatureIconProps) {
  const id = useId();
  return (
    <svg
      width={size} height={size} viewBox="0 0 64 64"
      className={className} role="img" aria-hidden="true" style={depth}
    >
      <defs>
        <radialGradient id={`${id}-cup`} cx="42%" cy="30%" r="75%">
          <stop offset="0%" stopColor="#fff4c2" />
          <stop offset="40%" stopColor="#ffd24a" />
          <stop offset="78%" stopColor="#e89a17" />
          <stop offset="100%" stopColor="#b5710c" />
        </radialGradient>
        <linearGradient id={`${id}-base`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffd667" />
          <stop offset="100%" stopColor="#c07e10" />
        </linearGradient>
      </defs>
      <path d="M17 17 C7 17 7 31 19 31" fill="none" stroke="#e7a31c" strokeWidth="3.4" strokeLinecap="round" />
      <path d="M47 17 C57 17 57 31 45 31" fill="none" stroke="#e7a31c" strokeWidth="3.4" strokeLinecap="round" />
      <path d="M17 11 H47 V19 C47 31 41 38 32 38 C23 38 17 31 17 19 Z" fill={`url(#${id}-cup)`} />
      <path d="M17 11 H47 V13 H17 Z" fill="#fff1bd" opacity="0.75" />
      <path d="M32 18 l2.1 4.4 4.8.6 -3.5 3.3 .9 4.8 -4.3 -2.3 -4.3 2.3 .9 -4.8 -3.5 -3.3 4.8 -.6 Z"
        fill="#fff4c2" opacity="0.92" />
      <ellipse cx="25" cy="18" rx="3.2" ry="6" fill="#fffbe8" opacity="0.5" />
      <rect x="29.5" y="38" width="5" height="7" fill={`url(#${id}-base)`} />
      <path d="M23 52 C23 47 26 45 32 45 C38 45 41 47 41 52 Z" fill={`url(#${id}-base)`} />
      <rect x="20" y="51" width="24" height="4.5" rx="2" fill={`url(#${id}-base)`} />
    </svg>
  );
}

type MedalVariant = 'gold' | 'silver' | 'bronze';

const MEDAL_PALETTE: Record<MedalVariant, { stops: [string, string, string, string]; star: string }> = {
  gold: { stops: ['#fff2bf', '#ffd14a', '#e08e12', '#a8650a'], star: '#b5710c' },
  silver: { stops: ['#ffffff', '#e8edf2', '#b9c2cc', '#8a96a3'], star: '#6b7682' },
  bronze: { stops: ['#ffe2c4', '#e8a169', '#c97b3b', '#9a5a26'], star: '#7a4a22' },
};

/** Podium / rank medal — gold, silver or bronze disc with ribbons and star. */
export function MedalIcon({ size = 48, className, variant = 'gold' }: FeatureIconProps & { variant?: MedalVariant }) {
  const id = useId();
  const { stops, star } = MEDAL_PALETTE[variant];
  return (
    <svg
      width={size} height={size} viewBox="0 0 64 64"
      className={className} role="img" aria-hidden="true" style={depth}
    >
      <defs>
        <radialGradient id={`${id}-disc`} cx="40%" cy="34%" r="72%">
          <stop offset="0%" stopColor={stops[0]} />
          <stop offset="42%" stopColor={stops[1]} />
          <stop offset="80%" stopColor={stops[2]} />
          <stop offset="100%" stopColor={stops[3]} />
        </radialGradient>
      </defs>
      <path d="M22 6 L31 30 L24 30 L16 9 Z" fill="#2f6df0" />
      <path d="M42 6 L33 30 L40 30 L48 9 Z" fill="#e0413a" />
      <circle cx="32" cy="40" r="19" fill={`url(#${id}-disc)`} />
      <circle cx="32" cy="40" r="15" fill="none" stroke="#fff3c4" strokeWidth="1.4" opacity="0.7" />
      <path d="M32 31 l2.5 5.2 5.7.7 -4.2 3.9 1.1 5.7 -5.1 -2.8 -5.1 2.8 1.1 -5.7 -4.2 -3.9 5.7 -.7 Z"
        fill={star} opacity="0.85" />
      <ellipse cx="25" cy="34" rx="4" ry="6.5" fill="#fffbe8" opacity="0.45" />
    </svg>
  );
}
